import React, { useEffect, useState } from "react";
import CommonModal from "../../../components/shared/modalLayout/CommonModal";
import { Row, Col } from "react-bootstrap";
import SelectBox from "../../../components/shared/fields/SelectBox ";
import InputWithLabel from "../../../components/shared/fields/InputWithLabel";
import Button from "../../../components/shared/buttons/button";
import InputWithIcon from "../../../components/shared/fields/InputWithIcon";
import { RefreshSvg } from "../../../svgFiles/RefreshSvg";
import {
  useCreateRecordMutation,
  useUpdateRecordMutation,
} from "./coldstorageApi";
import { useGetRoomsQuery } from "../ripeningRooms/ripeningApi";
import toast from "react-hot-toast";
import { useGetRipeningSummaryQuery } from "../dashboard/ripeingDashboardApi";
import { formatDateTimeModal } from "../../../components/shared/utils";
import MultiSelectBox from "../../../components/shared/fields/MultiSelectBox";
import useTranslator from "../../../hooks/useTranslator";
import { convertToUserTimezone, formatPalletName, getCurrentTime } from "../../../helpers/Utils";

const ColdStorageModal = ({ show, setShow, record, mode }) => {
  const { _t } = useTranslator();
  const [createRecord, { isLoading: isCreating }] = useCreateRecordMutation();
  const [updateRecord, { isLoading: isUpdating }] = useUpdateRecordMutation();
  const { data: roomList } = useGetRoomsQuery();
  const [enteredCases, setEnteredCases] = useState("");
  const [status, setStatus] = useState("");
  const [tab, setTab] = useState("cold_storage");
  const { data: summaryData } = useGetRipeningSummaryQuery({ tab });
  const [disabledRooms, setDisabledRooms] = useState([]);

  const initialFormData = {
    pallet_code: "",
    entered_date_time: convertToUserTimezone(null),
    expected_date_to_ripening: null,
    case_entered: enteredCases,
    fruit_entered_in_kg: "",
    exit_date_time_to_ripening: convertToUserTimezone(null),
    status: "cold_stored",
    room_id: "",
  };
  const [formData, setFormData] = useState(initialFormData);
    
  useEffect(() => {
    if (mode === "edit" || (mode === "view" && record)) {
      const updatedFormData = { ...initialFormData };

      for (const key in record) {
        if (key in initialFormData) {
          if (key === "entered_date_time") {
            updatedFormData[key] = convertToUserTimezone(record[key]);
          } else if (key === "expected_date_to_ripening") {
            updatedFormData[key] = convertToUserTimezone(record[key]);
          } else if (key === "exit_date_time_to_ripening") {
            updatedFormData[key] = convertToUserTimezone(record[key],'YYYY-MM-DDTHH:mm');
          } else {
            updatedFormData[key] = record[key];
          }
        }
      }

      setEnteredCases(record.case_entered);
      setFormData(updatedFormData);
    } else {
      setFormData(initialFormData);
    }
  }, [mode, record, show]);

  useEffect(() => {
    if (formData?.status === "ripening") {
      if (!formData?.exit_date_time_to_ripening) {
        setFormData((prev) => ({
          ...prev,
          exit_date_time_to_ripening: convertToUserTimezone(null),
        }));
      }
    }
  }, [formData?.status]);

  const handleClose = () => {
    setShow(false);
  };
  const calculateTotalCases = (fruitKg) => {
    return Math.ceil(fruitKg / 25) || "";
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    // prevent double submission
    if (isCreating || isUpdating) return;
    const currentTime = getCurrentTime();
    const updatedFormData = {
      ...formData,
      expected_date_to_ripening: `${
        formData.expected_date_to_ripening.split(" ")[0]
      } ${currentTime}`,
    };

    const mutation = mode === "add" ? createRecord : updateRecord;

    if (
      mode === "add" &&
      updatedFormData.case_entered > summaryData?.data.total_cases_on_floor
    ) {
      toast.error(_t("cases_exceed_floor_available", "The number of cases cannot exceed the cases available on the floor."));
      return;
    }

    if (mode !== "add") {
      let initialValue = record.case_entered;
      if (
        updatedFormData.case_entered > initialValue &&
        summaryData?.data.total_cases_on_floor <
          updatedFormData.case_entered - initialValue
      ) {
        toast.error(_t("cases_exceed_floor_available", "The number of cases cannot exceed the cases available on the floor."));
        return;
      }

      const selectedRoom = roomList?.data?.find(
        (room) => room.id == updatedFormData.room_id
      );
      if (selectedRoom) {
        const leftCasesCapacity =
          selectedRoom.cases_capacity - selectedRoom.total_cases;
        if (updatedFormData.case_entered > leftCasesCapacity) {
          toast.error(_t("room_max_capacity_reached", "The room has reached its maximum capacity."));
          return;
        }
      }
    }    

    try {
      const payload =
        mode === "add"
          ? updatedFormData
          : {
              id: record.id,
              requestBody: {
                ...updatedFormData,
                request_from: "cold_storage",
              },
            };
      await mutation(payload).unwrap();
      handleClose();
    } catch (error) {
      console.error("Error creating/updating Cold storage:", error);
    }
  };
  const handleMultiSelectChange = (selectedRooms) => {
    const casesEntered = Number(formData.case_entered);

    if (!casesEntered || casesEntered <= 0) {
      alert("Please enter number of cases first.");
      return;
    }

    let remainingCases = casesEntered;
    const roomDistribution = {};
    let totalSelectedCapacity = 0;
    const selectedRoomData = roomList?.data.filter((room) =>
      selectedRooms.includes(room.id)
    );

    selectedRoomData.forEach((room) => {
      const availableCapacity = room.cases_capacity - room.total_cases;
      const casesForRoom = Math.min(remainingCases, availableCapacity);
      roomDistribution[room.id] = casesForRoom;
      remainingCases -= casesForRoom;
      totalSelectedCapacity += availableCapacity;
    });

    if (remainingCases > 0) {
      toast.error(
        `Not enough room capacity for ${casesEntered} cases. ${remainingCases} remaining. Please select additional room(s).`
      );
    }
    const shouldDisable = totalSelectedCapacity >= casesEntered;

    const disableRooms = shouldDisable
      ? roomList?.data
          .filter((room) => !selectedRooms.includes(room.id))
          .map((room) => room.id)
      : [];

    setDisabledRooms(disableRooms);

    setFormData((prevFormData) => ({
      ...prevFormData,
      room_id: selectedRooms,
      room_distribution: roomDistribution,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "status") {
      setStatus(value);
    }
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const renderFormFields = () => {
    return mode === "add" ? (
      <>
        <Col lg={6}>
          <InputWithLabel
            label={_t("pallet_code", "Pallet Code")}
            type="text"
            name="pallet_code"
            value={formData.pallet_code}
            placeholder={_t("pallet_placeholder", "PXX")}
            onChange={handleChange}
          />
        </Col>
        <Col lg={6}>
          <InputWithLabel
            label={_t("entered_date_time", "Entered Date/Time")}
            type="text"
            name="entered_date_time"
            value={formData.entered_date_time}
            disabled={true}
            onChange={handleChange}
          />
        </Col>
        <Col lg={6}>
          <InputWithLabel
            label={_t(
              "expected_date_ripening",
              "Expected Date to Move in Ripening Room"
            )}
            type="date"
            min={convertToUserTimezone(null, "YYYY-MM-DD")}
            name="expected_date_to_ripening"
            value={formData.expected_date_to_ripening}
            onChange={handleChange}
            disabled={mode === "view"}
          />
        </Col>
        <Col lg={6}>
          <InputWithLabel
            label={_t("fruit_kg", "Fruit entered in KG")}
            placeholder={"0"}
            type="number"
            name="fruit_entered_in_kg"
            // icon={RefreshSvg}
            value={formData.fruit_entered_in_kg}
            onChange={handleChange}
            disabled={mode === "view"}
          />
        </Col>
        <Col lg={6}>
          <InputWithLabel
            label={_t("case_entered", "Case Entered")}
            placeholder={_t("case_placeholder", "Case Entered...")}
            type="text"
            name="case_entered"
            value={enteredCases}
            onChange={handleChange}
            disabled={true}
          />
        </Col>

        <Col lg={6}>
          <SelectBox
            label={_t("status", "Status")}
            type="text"
            name="status"
            value={formData.status}
            options={[
              { value: "cold_stored", label: _t("cold_stored", "Cold Stored") },
              { value: "ripening", label: _t("ripening", "Ripening") },
            ]}
            onChange={handleChange}
            disabled={true}
          />
        </Col>
      </>
    ) : (
      <>
        <Col lg={6}>
          <InputWithLabel
            label={_t("pallet_code", "Pallet Code")}
            type="text"
            name="pallet_code"
            value={formatPalletName(formData.pallet_code)}
            disabled={true}
          />
        </Col>
        <Col lg={6}>
          <InputWithLabel
            label={_t("entered_date_time", "Entered Date/Time")}
            type="text"
            name="entered_date_time"
            value={formData.entered_date_time}
            disabled={true}
            onChange={handleChange}
          />
        </Col>
        {/* <Col lg={6}>
          <InputWithLabel
            label={_t(
              "expected_date_ripening",
              "Expected Date to Move in Ripening Room"
            )}
            type="date"
            min={new Date().toISOString().split("T")[0]}
            name="expected_date_to_ripening"
            value={formData.expected_date_to_ripening}
            onChange={handleChange}
            disabled={mode === "view"}
          />
        </Col> */}
        <Col lg={6}>
          <InputWithIcon
            label={_t("fruit_kg", "Fruit entered in KG")}
            type="number"
            name="fruit_entered_in_kg"
            // icon={RefreshSvg}
            value={formData.fruit_entered_in_kg}
            onChange={handleChange}
            disabled={true} //mode === "view"
            placeholder={"0"}
          />
        </Col>
        <Col lg={6}>
          <InputWithLabel
            label={_t("case_entered", "Case Entered")}
            type="number"
            name="case_entered"
            value={formData.case_entered}
            onChange={handleChange}
            disabled={true}
            placeholder={"0"}
          />
        </Col>
        <Col lg={6}>
          <SelectBox
            label={_t("status", "Status")}
            type="text"
            name="status"
            value={formData.status}
            options={[
              { value: "cold_stored", label: _t("cold_stored", "Cold Stored") },
              { value: "ripening", label: _t("ripening", "Ripening") },
            ]}
            onChange={handleChange}
            disabled={mode === "view"}
          />
        </Col>
        {status === "ripening" && (
          <Col lg={6}>
            <InputWithLabel
              label={_t("exit_date_time", "Exit Date and Time")}
              type="datetime-local"
              name="exit_date_time_to_ripening"
              value={formData.exit_date_time_to_ripening}
              onChange={handleChange}
              min={formData?.entered_date_time}
              disabled={true}
            />
          </Col>
        )}
        {status === "ripening" && (
          <Col lg={6}>
            <SelectBox
              label={_t("select_room", "Select Room")}
              type="text"
              name="room_id"
              value={formData.room_id}
              options={roomList?.data.map((list) => ({
                value: list.id,
                label: `${list.name}-(${
                  list.cases_capacity - list.total_cases
                } capacity)`,
              }))}
              onChange={handleChange}
              disabled={mode === "view"}
            />
          </Col>
        )}
        {/* {status === "ripening" && (
          <Col lg={6}>
            <MultiSelectBox
              label="Select Room"
              type="text"
              name="room_id"
              value={formData.room_id}
              options={roomList?.data.map((list) => ({
                value: list.id,
                label: `${list.name}-(${
                  list.cases_capacity - list.total_cases
                } capacity)`,
                isDisabled: disabledRooms.includes(list.id),
              }))}
              onChange={handleMultiSelectChange}
              disabled={mode === "view"}
            />
          </Col>
        )} */}
      </>
    );
  };

  const getRawMaterialLabel = (mode) => {
    switch (mode) {
      case "add":
        return _t("move_to_cold_storage", "Move Pallet to Cold Storage");
      case "edit":
        return _t("move_to_ripening", "Move Pallet to Ripening Room");
      default:
        return _t("raw_material", "Raw Material");
    }
  };
  return (
    <CommonModal
      show={show}
      handleClose={handleClose}
      setShow={setShow}
      title={getRawMaterialLabel(mode)}
      children={formData}
    >
      <Row>{renderFormFields()}</Row>

      <Row>
        <Col className="text-end">
          {mode !== "view" && (
            <Button
              label={_t("save_changes", "Save Changes")}
              size="small"
              onClick={handleSubmit}
              disabled={isCreating || isUpdating}
            />
          )}
        </Col>
      </Row>
    </CommonModal>
  );
};

export default ColdStorageModal;
